import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Instagram, X } from 'lucide-react';
import { useLanguage } from '../LanguageProvider';

export function Footer() {
  const { language } = useLanguage();
  const texts = {
    tr: {
      company: 'İLİMERA Teknoloji Yazılım',
      description:
        'Yenilikçi elektronik ve yazılım çözümleri ile teknoloji dünyasında güvenilir çözümler sunuyoruz. Müşteri odaklı yaklaşımımızla sektörde öncü olmaya devam ediyoruz.',
      hq: 'Genel Merkez',
      rd: 'Ar-Ge Merkezi',
      quickLinks: 'Hızlı Linkler',
      home: 'Ana Sayfa',
      about: 'Hakkımızda',
      products: 'Ürünlerimiz',
      contact: 'İletişim',
      social: 'Sosyal Medya',
      copyright: '© 2024 İLİMERA Tüm hakları saklıdır.',
      privacy: 'Gizlilik ve Çerez Politikası',
      kvkk: 'KVKK Aydınlatma Metni',
    },
    en: {
      company: 'İLİMERA Technology Software',
      description:
        'We deliver reliable electronic and software solutions with innovative technology. Our customer-first approach keeps us at the forefront of the industry.',
      hq: 'Office',
      rd: 'R&D Center',
      quickLinks: 'Quick Links',
      home: 'Home',
      about: 'About',
      products: 'Products',
      contact: 'Contact',
      social: 'Social Media',
      copyright: '© 2024 İLİMERA All rights reserved.',
      privacy: 'Privacy and Cookie Policy',
      kvkk: 'GDPR Information Notice',
    },
  } as const;

  const t = texts[language] ?? texts.tr;

  return (
    <footer className="relative text-white overflow-hidden">
      {/* Background image */}
      <div aria-hidden className="absolute inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: "url('/hero1-bg.jpg')" }}></div>
      {/* Dark overlay */}
      <div aria-hidden className="absolute inset-0 z-[5] bg-black/70"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center mb-4">
              <span className="text-xl font-bold">{t.company}</span>
            </div>
            <p className="text-gray-300 mb-6 max-w-md">
              {t.description}
            </p>
            <div className="space-y-2">
              <div className="flex items-center text-gray-300">
                <MapPin size={16} className="mr-2" />
                <span>
                  <strong>{t.hq}</strong>
                  <br />
                  Akdeniz Üniversitesi, Teknokent Uluğbey ArGe-2 No:3A/B96  
                  <br />
                  Konyaaltı / ANTALYA
                </span>
              </div>
              <div className="flex items-center text-gray-300">
                <MapPin size={16} className="mr-2" />
                <span>
                  <strong>{t.rd}</strong>
                  <br />
                  Davraz Mah. 104. Cadde No:44-A Kat:2 Ofis:13/1
                  <br />
                  Merkez / ISPARTA
                </span>
              </div>
              <div className="flex items-center text-gray-300">
                <Phone size={16} className="mr-2" />
                <span>+90 (542) 549 85 27</span>
              </div>
              <div className="flex items-center text-gray-300">
                <Mail size={16} className="mr-2" />
                <span>info@ilimera.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4">{t.quickLinks}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-white transition-colors">
                  {t.home}
                </Link>
              </li>
              <li>
                <Link to="/hakkimizda" className="text-gray-300 hover:text-white transition-colors">
                  {t.about}
                </Link>
              </li>
              <li>
                <Link to="/urunler" className="text-gray-300 hover:text-white transition-colors">
                  {t.products}
                </Link>
              </li>
              <li>
                <Link to="/iletisim" className="text-gray-300 hover:text-white transition-colors">
                  {t.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="font-semibold mb-4">{t.social}</h3>
            <div className="flex space-x-4">
              <a 
                href="https://www.linkedin.com/company/i%CC%87li%CC%87mera/" 
                target="_blank"
                className="text-gray-300 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://www.instagram.com/ilimera_teknoloji/" 
                className="text-gray-300 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-300 text-sm">
              {t.copyright}
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/cerez-politikasi" className="text-gray-300 hover:text-white text-sm transition-colors">
                {t.privacy}
              </Link>
              <Link to="/kvkk-iletisim-aydinlatma" className="text-gray-300 hover:text-white text-sm transition-colors">
                {t.kvkk}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}